import React, { useEffect, useRef, useState } from 'react';
import { BackgroundSettings } from '../types';
import { generateBackgroundCSS, getPresetThemeColors, applyThemeColors } from '../services/backgroundService';
import ParticleEffect from './ParticleEffect';

interface BackgroundRendererProps {
  settings: BackgroundSettings;
}

const BackgroundRenderer: React.FC<BackgroundRendererProps> = ({ settings }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoError, setVideoError] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  // CRITICAL FIX: Clear body background from old themeService
  useEffect(() => {
    // Remove old theme service background that causes the persistent image
    document.body.style.backgroundImage = 'none';
    document.body.style.background = 'transparent';
  }, []);

  // Apply theme colors when preset changes or on mount
  useEffect(() => {
    if (settings.type === 'preset' && settings.presetId) {
      const themeColors = getPresetThemeColors(settings.presetId);
      if (themeColors) {
        applyThemeColors(themeColors);
        // CRITICAL FIX: Also save that we're using background theme to prevent themeService override
        localStorage.setItem('active_background_theme', settings.presetId);
      }
    } else {
      // Clear active background theme if not using a preset
      localStorage.removeItem('active_background_theme');
    }
  }, [settings.type, settings.presetId]);

  // Parallax effect
  useEffect(() => {
    if (!settings.parallaxEnabled) {
      setScrollY(0); // Reset scroll position when parallax is disabled
      return;
    }

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [settings.parallaxEnabled]);

  // Auto-play video
  useEffect(() => {
    if (settings.type === 'video' && videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.error('Video autoplay failed:', error);
        setVideoError(true);
      });
    }
  }, [settings.type, settings.videoUrl]);

  // Generate fresh background styles - don't mutate
  const backgroundStyles = generateBackgroundCSS(settings);
  
  // Create separate parallax styles to avoid mutation
  const parallaxStyles: React.CSSProperties = settings.parallaxEnabled && scrollY > 0 
    ? {
        ...backgroundStyles,
        transform: `translateY(${scrollY * 0.5}px)`,
        transition: 'transform 0.1s ease-out',
      }
    : backgroundStyles;

  return (
    <>
      {/* Main Background Layer - SINGLE SOURCE OF TRUTH */}
      {settings.type === 'video' && settings.videoUrl && !videoError ? (
        <div 
          key="video-background" 
          className="fixed top-0 left-0 w-screen h-screen overflow-hidden" 
          style={{ zIndex: -2 }}
        >
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            style={{
              filter: settings.blurIntensity > 0 ? `blur(${settings.blurIntensity}px)` : undefined,
              transform: settings.parallaxEnabled && scrollY > 0 ? `translateY(${scrollY * 0.5}px)` : undefined,
              transition: settings.parallaxEnabled ? 'transform 0.1s ease-out' : undefined,
            }}
            loop
            muted
            playsInline
            autoPlay
          >
            <source src={settings.videoUrl} type="video/mp4" />
            <source src={settings.videoUrl} type="video/webm" />
          </video>
        </div>
      ) : (
        <div 
          key={`background-${settings.type}-${settings.presetId || 'custom'}`}
          className="fixed top-0 left-0 w-screen h-screen" 
          style={parallaxStyles} 
        />
      )}

      {/* Dark Overlay for Readability */}
      {settings.overlayOpacity > 0 && (
        <div
          className="fixed top-0 left-0 w-screen h-screen pointer-events-none"
          style={{
            backgroundColor: `rgba(0, 0, 0, ${settings.overlayOpacity / 100})`,
            zIndex: -1,
          }}
        />
      )}

      {/* Particle Effects Layer */}
      {settings.particleEffect !== 'none' && (
        <ParticleEffect type={settings.particleEffect} intensity={settings.particleIntensity} />
      )}
    </>
  );
};

export default BackgroundRenderer;

